import React, { useEffect, useMemo, useState } from "react";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import generalIcon from "../../../../assets/images/general.png";
import shiftAIcon from "../../../../assets/images/shifta.png";
import shiftBIcon from "../../../../assets/images/shiftb.png";
import shiftCIcon from "../../../../assets/images/shiftc.png";
import { getShiftWiseSummary } from "../../../Apis/DashboardApis/primaryDashboardApi";
import CardLoader from "../../../Common/ShimmerLoader/CardLoader";
import DashboardSectionCard from "./shared/DashboardSectionCard";
import DashboardFilters from "./shared/DashboardFilters";
import PerformanceRing from "./shared/PerformanceRing";
import useDashboardFilters from "./shared/useDashboardFilters";

const SHIFT_META = {
  general: {
    iconSrc: generalIcon,
    color: "#16a34a",
    namePrefix: "GENERAL SHIFT",
  },
  a: {
    iconSrc: shiftAIcon,
    color: "#2563eb",
    namePrefix: "A SHIFT",
  },
  b: {
    iconSrc: shiftBIcon,
    color: "#7c3aed",
    namePrefix: "B SHIFT",
  },
  c: {
    iconSrc: shiftCIcon,
    color: "#ea580c",
    namePrefix: "C SHIFT",
  },
};

const SHIFT_ID_META = {
  13: SHIFT_META.general,
  1: SHIFT_META.a,
  2: SHIFT_META.b,
  3: SHIFT_META.c,
};

const getShiftMeta = (shift = {}) => {
  const name = String(shift.shiftName || "").toLowerCase();

  if (name.includes("general")) {
    return SHIFT_META.general;
  }

  if (name.includes("shift a") || name === "a shift") {
    return SHIFT_META.a;
  }

  if (name.includes("shift b") || name === "b shift") {
    return SHIFT_META.b;
  }

  if (name.includes("shift c") || name === "c shift") {
    return SHIFT_META.c;
  }

  if (SHIFT_ID_META[shift.shiftId]) {
    return SHIFT_ID_META[shift.shiftId];
  }

  return {
    iconSrc: generalIcon,
    color: "#2563eb",
    namePrefix: String(shift.shiftName || "SHIFT").toUpperCase(),
  };
};

const formatCount = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  return Number(value);
};

const buildShifts = (shifts) => {
  const list = Array.isArray(shifts) ? shifts : [];

  return list.map((item) => {
    const meta = getShiftMeta(item);

    return {
      key: item.shiftId ?? item.shiftName,
      name: meta.namePrefix,
      iconSrc: meta.iconSrc,
      color: meta.color,
      planned: formatCount(item.plannedCount),
      present: formatCount(item.actualCount),
      absent: formatCount(item.absentCount),
      performance: formatCount(item.performancePercentage),
      time:
        item.startTime && item.endTime
          ? `${item.startTime} - ${item.endTime}`
          : "-",
    };
  });
};

const ShiftWiseSummary = ({ wards, shifts: shiftsProp }) => {
  const {
    filters,
    appliedFilters,
    handleFilterChange,
    handleSearch,
  } = useDashboardFilters();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(!shiftsProp);

  useEffect(() => {
    if (Array.isArray(shiftsProp)) {
      return undefined;
    }

    let isMounted = true;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await getShiftWiseSummary(appliedFilters);

        if (isMounted && response?.status) {
          setShifts(response?.data?.shifts ?? []);
        }
      } catch (error) {
        console.error("Failed to load shift wise summary:", error);
        if (isMounted) {
          setShifts([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      isMounted = false;
    };
  }, [appliedFilters, shiftsProp]);

  const list = useMemo(() => {
    if (Array.isArray(shiftsProp)) {
      return shiftsProp;
    }

    return buildShifts(shifts);
  }, [shifts, shiftsProp]);

  return (
    <DashboardSectionCard
      title="Shift Wise Summary"
      className="pd-shift-section"
      filters={
        <DashboardFilters
          className="pd-shift-filters"
          searchVariant="inline"
          fromDate={filters?.fromDate}
          toDate={filters?.toDate}
          ward={filters?.ward}
          wards={wards}
          onFromDateChange={(value) => handleFilterChange("fromDate", value)}
          onToDateChange={(value) => handleFilterChange("toDate", value)}
          onWardChange={(value) => handleFilterChange("ward", value)}
          onSearch={handleSearch}
        />
      }
    >
      {loading ? (
        <CardLoader count={4} />
      ) : (
        <div className="pd-metric-row cols-4 pd-shift-row">
          {list.map((shift) => (
            <div className="pd-shift-card" key={shift.key ?? shift.name}>
              <div className="pd-shift-header">
                <span className="pd-shift-header-icon">
                  <img src={shift.iconSrc} alt={shift.name} className="pd-shift-icon-img" />
                </span>
                <div className="pd-shift-name" style={{ color: shift.color }}>
                  {shift.name}
                  <span>SUMMARY</span>
                </div>
              </div>

              <div className="pd-shift-stats">
                <div>
                  <span>Planned (MIOP)</span>
                  <strong className="pd-shift-planned">{shift.planned}</strong>
                </div>
                <div>
                  <span>Actual Present</span>
                  <strong className="pd-shift-present">{shift.present}</strong>
                </div>
                <div>
                  <span>Actual Absent</span>
                  <strong className="pd-shift-absent">{shift.absent}</strong>
                </div>
              </div>

              <div className="pd-shift-performance">
                <PerformanceRing percent={shift.performance} color="#15803d" />
              </div>

              <div className="pd-shift-time">
                <FontAwesomeIcon icon={faClock} />
                {shift.time}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardSectionCard>
  );
};

export default ShiftWiseSummary;
